import { makeStyles, Slider } from "@material-ui/core";
import { useEffect, useState, useCallback } from "react";

const questions = [
  {
    key: "socks",
    text: "Putting on your socks or tights without help or aids (e.g sock aid).",
  },
  {
    key: "bFromWaist",
    text: "Bending from the waist to pick up a pen from the floor without aid.",
  },
  {
    key: "highShelf",
    text: "How would you describe the overall level of pain/swelling in joints other than neck, back or hips you have had?",
  },
  {
    key: "upFromChair",
    text: "Getting up from an armless chair without your hands or any other help.",
  },
  {
    key: "upOffFloor",
    text: "Getting up off the floor without help from lying on your back.",
  },
  {
    key: "standing",
    text: "Standing unsupported for 10 minutes without discomfort.",
  },
  {
    key: "climbing",
    text: "Climbing 12-15 steps without using a handrail or walking aid.",
  },
  {
    key: "lookingOverShoulder",
    text: "Looking over your shoulder without turning your body.",
  },
  {
    key: "physicalActivity",
    text: "Doing physically demanding activities (e.g physiotherapy exercises, gardening or sports).",
  },
  {
    key: "fullDayActivity",
    text: "Doing a full day’s activity wether it be at home or at work.",
  },
];

const defaultAssessment = {
  socks: 1,
  bFromWaist: 1,
  highShelf: 1,
  upFromChair: 1,
  upOffFloor: 1,
  standing: 1,
  climbing: 1,
  lookingOverShoulder: 1,
  physicalActivity: 1,
  fullDayActivity: 1,
};

const marks = Array.from({ length: 10 }, (_, i) => ({
  value: i + 1,
  label: (i + 1).toString(),
}));

const msTimeMarks = [
  { value: 1, label: "0 hr" },
  { value: 5, label: "1 hr" },
  { value: 10, label: "2+ hrs" },
];

const BasfiAssessment = ({
  appointmentId,
  pid,
  token,
  currentBasfiInfo,
  onBasfiInfoUpdate,
  onError,
}) => {
  const [assessmentDetail, setAssessmentDetail] = useState(defaultAssessment);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);

  // Fetch current BASFI score and details (only if not already available)
  const fetchBasfiPainScore = useCallback(async () => {
    if (!pid || !token) return;

    try {
      const response = await fetch(`/api/v1/pain-score/basfi/${pid}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const responseData = await response.json();

      if (responseData?.status?.success) {
        responseData.current && onBasfiInfoUpdate(responseData.current);
      }
    } catch (error) {
      onError(error);
    } finally {
    }
  }, [pid, token, onBasfiInfoUpdate, onError]);

  // Calculate and update BASFI score
  const calculateAndUpdateBasfi = useCallback(
    async (assessmentData) => {
      if (!appointmentId || !token) return;

      setIsCalculating(true);
      try {
        const response = await fetch(
          `/api/v1/pain-score/basfi/${appointmentId}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(assessmentData),
          }
        );

        const responseData = await response.json();

        if (responseData?.status?.success) {
          // Refetch the updated score after a short delay
          await fetchBasfiPainScore();
        } else {
          onError(new Error("Failed to calculate BASFI score"), responseData);
        }
      } catch (error) {
        onError(error);
      } finally {
        setIsCalculating(false);
      }
    },
    [appointmentId, token, fetchBasfiPainScore, onError]
  );

  useEffect(() => {
    if (currentBasfiInfo?.value && currentBasfiInfo?.scoreDetail) {
      return; // Skip fetch if data already available
    }

    setIsInitialLoading(true);
    fetchBasfiPainScore().finally(() => {
      setIsInitialLoading(false);
    });
  }, [fetchBasfiPainScore]); //

  useEffect(() => {
    // Skip initial fetch if both score and assessment details are already available
    if (currentBasfiInfo?.value && currentBasfiInfo?.scoreDetail) {
      // Use provided data to populate assessment details
      setAssessmentDetail({
        socks: currentBasfiInfo.scoreDetail.socks || 1,
        bFromWaist: currentBasfiInfo.scoreDetail.bFromWaist || 1,
        highShelf: currentBasfiInfo.scoreDetail.highShelf || 1,
        upFromChair: currentBasfiInfo.scoreDetail.upFromChair || 1,
        upOffFloor: currentBasfiInfo.scoreDetail.upOffFloor || 1,
        standing: currentBasfiInfo.scoreDetail.standing || 1,
        climbing: currentBasfiInfo.scoreDetail.climbing || 1,
        lookingOverShoulder:
          currentBasfiInfo.scoreDetail.lookingOverShoulder || 1,
        physicalActivity: currentBasfiInfo.scoreDetail.physicalActivity || 1,
        fullDayActivity: currentBasfiInfo.scoreDetail.fullDayActivity || 1,
      });
    }
  }, [currentBasfiInfo]);

  // Handle slider changes
  const handleSliderChange = useCallback((key, newValue) => {
    setAssessmentDetail((prev) => ({
      ...prev,
      [key]: newValue,
    }));
  }, []);

  // Styles
  const useStyles = makeStyles({
    root: { height: 10, marginTop: 0 },
    thumb: {
      height: 24,
      width: 24,
      backgroundColor: "var(--darkbluecolorone)",
      marginTop: -10,
      marginLeft: -12,
    },
    track: {
      height: 5,
      borderRadius: 5,
      backgroundColor: "var(--darkbluecolorone)",
    },
    rail: {
      height: 5,
      borderRadius: 5,
    },
    valueLabel: {
      left: "calc(-50% + 8px)",
      top: -30,
      color: "var(--darkbluecolorone)",
    },
  });
  const sliderClasses = useStyles();

  const useComponentStyles = makeStyles({
    label: {
      fontSize: "16px",
      lineHeight: "21px",
      fontWeight: "normal",
      fontStyle: "normal",
      letterSpacing: "0px",
      color: "#000000CC",
      opacity: 1,
    },
    heading: {
      color: "#0060A6",
      backgroundColor: "#E8ECEF",
      fontSize: "18px",
    },
    scoreBlock: {
      backgroundColor: "#0060A6",
      color: "#FFFFFF",
      padding: "10px 20px",
      fontSize: "20px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
    },
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "20px",
    },
  });
  const componentClasses = useComponentStyles();

  if (isInitialLoading) {
    return (
      <div className={componentClasses.loadingContainer}>
        <div id="loader" />
      </div>
    );
  }

  return (
    <>
      <div className={componentClasses.heading + " text-center px-4 py-3"}>
        The Bath Ankylosing Spondylitis Functional Index (BASFI)
      </div>

      <div className="text-center px-4 mt-2">
        <span className="fw-bold">Difficulty on a 10 point scale</span>
        <br />
        <span>(1 is easy and 10 is the impossible)</span>
      </div>
      <div className="text-center px-4 mt-2 text-muted small">
        Please indicate your level of ability with each of the following
        activities during the past week.
      </div>

      <div className="container p-4">
        {questions.map((question) => (
          <div className="mb-4" key={question.key}>
            <label className={componentClasses.label}>{question.text}</label>
            <div className="text-muted small mt-2 d-flex justify-content-between">
              <span>Easy</span>
              <span>Impossible</span>
            </div>
            <Slider
              classes={sliderClasses}
              value={assessmentDetail[question.key]}
              onChange={(_, newValue) =>
                handleSliderChange(question.key, newValue)
              }
              step={1}
              min={1}
              max={10}
              marks={question.key === "msTime" ? msTimeMarks : marks}
              valueLabelDisplay="auto"
              disabled={isCalculating}
            />
          </div>
        ))}

        <div className="mb-4">
          <div className="btn_block">
            <button
              className="btn btn_primary m-auto"
              onClick={() => calculateAndUpdateBasfi(assessmentDetail)}
              disabled={isCalculating || !appointmentId}
            >
              {isCalculating ? "Calculating..." : "Calculate"}
            </button>
          </div>
        </div>
      </div>

      <div className={componentClasses.scoreBlock}>
        <span className="fw-bold">BASFI Score:</span>
        <span className="fw-bold">
          {currentBasfiInfo?.value !== null &&
          currentBasfiInfo?.value !== undefined
            ? currentBasfiInfo.value
            : "—"}
        </span>
      </div>
    </>
  );
};

export default BasfiAssessment;
